import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Typography,
  Box,
  LinearProgress,
  Tooltip,
  makeStyles,
  Slide,
} from "@material-ui/core";
import {
  CheckCircle,
  Cancel,
  Send,
  Close,
  QueueMusicOutlined,
  LoyaltyOutlined
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import MarkdownWrapper from "../MarkdownWrapper";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: 450,
      maxWidth: 600,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  suggestionItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: "flex-start",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  confidenceChip: {
    height: 20,
    fontSize: "0.65rem",
    marginRight: theme.spacing(0.5),
  },
  highConfidence: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  mediumConfidence: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  lowConfidence: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  acceptButton: {
    color: theme.palette.success.main,
  },
  rejectButton: {
    color: theme.palette.error.main,
  },
  sendButton: {
    color: theme.palette.primary.main,
  },
  noSuggestions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  loadingContainer: {
    padding: theme.spacing(2),
  },
  suggestion: {
    marginBottom: theme.spacing(1),
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AgentSuggestionsModal = ({
  open,
  onClose,
  suggestions = [],
  isLoading = false,
  onAcceptSuggestion,
  onRejectSuggestion,
  onSendSuggestion,
}) => {
  const classes = useStyles();

  const getConfidenceConfig = (confidence) => {
    if (confidence >= 0.8) {
      return {
        label: i18n.t("agentSuggestions.confidence.high"),
        className: classes.highConfidence,
      };
    } else if (confidence >= 0.5) {
      return {
        label: i18n.t("agentSuggestions.confidence.medium"),
        className: classes.mediumConfidence,
      };
    } else {
      return {
        label: i18n.t("agentSuggestions.confidence.low"),
        className: classes.lowConfidence,
      };
    }
  };

  const formatSuggestionText = (text) => {
    if (!text || typeof text !== 'string') {
      return '';
    }
    if (text.length > 200) {
      return text.substring(0, 200) + "...";
    }
    return text;
  };

  if (isLoading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        className={classes.dialog}
        TransitionComponent={Transition}
      >
        <DialogTitle>
          <Box className={classes.header}>
            <QueueMusicOutlined className={classes.headerIcon} />
            <Typography variant="h6">
              {i18n.t("agentSuggestions.generating")}
            </Typography>
            <Box flexGrow={1} />
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box className={classes.loadingContainer}>
            <LinearProgress />
            <Typography variant="body2" align="center" style={{ marginTop: 16 }}>
              {i18n.t("agentSuggestions.analyzing")}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      TransitionComponent={Transition}
      maxWidth="md"
    >
      <DialogTitle>
        <Box className={classes.header}>
          <QueueMusicOutlined className={classes.headerIcon} />
          <Typography variant="h6">
            {i18n.t("agentSuggestions.title")}
          </Typography>
          {suggestions.length > 0 && (
            <Chip
              size="small"
              label={suggestions.length}
              color="primary"
              style={{ marginLeft: 8 }}
            />
          )}
          <Box flexGrow={1} />
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {suggestions.length === 0 ? (
          <Box className={classes.noSuggestions}>
            <LoyaltyOutlined color="disabled" fontSize="large" />
            <Typography variant="body1" align="center">
              {i18n.t("agentSuggestions.noSuggestions")}
            </Typography>
          </Box>
        ) : (
          <List>
            {suggestions.map((suggestion) => {
              const confidenceConfig = getConfidenceConfig(suggestion.confidence);

              return (
                <ListItem
                  key={suggestion.id}
                  className={classes.suggestionItem}
                  alignItems="flex-start"
                >
                  <Box width="100%">
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip
                        size="small"
                        label={confidenceConfig.label}
                        className={`${classes.confidenceChip} ${confidenceConfig.className}`}
                      />
                      <Chip
                        size="small"
                        label={suggestion.type || "Response"}
                        variant="outlined"
                      />
                    </Box>

                    <Box className={classes.suggestion}>
                      <Typography variant="body1" component="div">
                        <MarkdownWrapper>
                          {formatSuggestionText(suggestion.message || '')}
                        </MarkdownWrapper>
                      </Typography>
                    </Box>

                    {suggestion.context && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        style={{ marginBottom: 8 }}
                      >
                        {i18n.t("agentSuggestions.context")}: {suggestion.context}
                      </Typography>
                    )}

                    <Box className={classes.actionButtons}>
                      <Tooltip title={i18n.t("agentSuggestions.accept")}>
                        <Button
                          size="small"
                          className={classes.acceptButton}
                          startIcon={<CheckCircle />}
                          onClick={() => {
                            if (onAcceptSuggestion) {
                              onAcceptSuggestion(suggestion);
                            }
                            onClose();
                          }}
                        >
                          {i18n.t("agentSuggestions.accept")}
                        </Button>
                      </Tooltip>

                      <Tooltip title={i18n.t("agentSuggestions.sendDirectly")}>
                        <Button
                          size="small"
                          className={classes.sendButton}
                          startIcon={<Send />}
                          onClick={() => {
                            if (onSendSuggestion) {
                              onSendSuggestion(suggestion);
                            }
                            onClose();
                          }}
                        >
                          {i18n.t("agentSuggestions.sendDirectly")}
                        </Button>
                      </Tooltip>

                      <Tooltip title={i18n.t("agentSuggestions.reject")}>
                        <Button
                          size="small"
                          className={classes.rejectButton}
                          startIcon={<Cancel />}
                          onClick={() => {
                            if (onRejectSuggestion) {
                              onRejectSuggestion(suggestion);
                            }
                          }}
                        >
                          {i18n.t("agentSuggestions.reject")}
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {i18n.t("buttons.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentSuggestionsModal;